/**
 * Die gelieferten Materialtexturen auf die gelieferten Rümpfe.
 *
 * Die Modelle tragen UV0 in Metern (`models/src/surfaces.ts`, jedes Material in einer
 * eigenen 512-Meter-Spalte), und die Kacheln der Lieferung stehen für 4 × 4 Meter. Die
 * App zeigt ein Modell aber in der Länge, die der Zustand nennt, und skaliert es dafür:
 * ein Frachter von 120 Metern ist dasselbe Modell wie einer von 180. Die UVs wachsen
 * dabei nicht mit, also muss die Skalierung in die Wiederholung der Textur eingehen,
 * sonst sind die Planken auf dem kurzen Schiff schmaler als auf dem langen.
 *
 * Zwei Dinge aus dem Kanalvertrag (`assets/texturen/README.md`) brauchen eine Hand:
 * die Rauheit steckt im Alphakanal, three liest sonst Grün, und der Deckatlas führt
 * Holz und Blech nebeneinander in einem Bild, das nicht als Ganzes kacheln darf.
 */
import { MeshStandardMaterial, RepeatWrapping, Texture } from 'three'
import { atlasHaelfte, textur, type TexturName, type TexturQuelle } from './texturen'

/** Kantenlänge der Materialkacheln in Metern, wie die Lieferung sie führt. */
export const KACHEL_M = 4

/** Die Materialnamen aus der Lieferung und die Textur, die dazu gehört. */
export type Oberflaeche = 'stahl' | 'deck-holz' | 'deck-stahl' | 'segel' | 'kunststoff' | 'glas'

const haelften = new Map<'links' | 'rechts', Texture | null>()

function deckHaelfte(seite: 'links' | 'rechts', quelle: TexturQuelle): Texture | undefined {
  const fertig = haelften.get(seite)
  if (fertig !== undefined) return fertig ?? undefined
  const atlas = quelle('deck')
  if (!atlas) return undefined
  const geschnitten = atlasHaelfte(atlas, seite) ?? null
  haelften.set(seite, geschnitten)
  return geschnitten ?? undefined
}

export interface Belegung {
  farbe?: Texture
  normale?: Texture
}

/** Welche Datei zu welchem Material gehört, und welche Hälfte des Deckatlas. */
export interface Zuordnung {
  farbe?: TexturName
  /** Nur beim Deck: der Atlas führt links Holz, rechts Blech. */
  haelfte?: 'links' | 'rechts'
  normale?: TexturName
}

/**
 * Die Tabelle ist die einzige Stelle, an der steht, welches Material welche Oberfläche
 * bekommt. Kunststoff und Glas stehen bewusst leer: die Lieferung führt für sie kein
 * Muster, und ein geborgtes wäre eine Behauptung über eine Oberfläche, die niemand
 * geliefert hat.
 */
export const ZUORDNUNG: Record<Oberflaeche, Zuordnung> = {
  stahl: { farbe: 'stahl', normale: 'stahl-normal' },
  'deck-holz': { farbe: 'deck', haelfte: 'links' },
  'deck-stahl': { farbe: 'deck', haelfte: 'rechts' },
  segel: { farbe: 'segeltuch' },
  kunststoff: {},
  glas: {},
}

export function belegungFuer(name: string, quelle: TexturQuelle = textur): Belegung {
  const zuordnung = ZUORDNUNG[name as Oberflaeche]
  if (!zuordnung?.farbe) return {}
  const farbe = zuordnung.haelfte ? deckHaelfte(zuordnung.haelfte, quelle) : quelle(zuordnung.farbe)
  return { farbe, normale: zuordnung.normale ? quelle(zuordnung.normale) : undefined }
}

/**
 * Wie oft eine Kachel auf einen Meter des Modells fällt.
 *
 * Die UVs stehen in Metern des ungestreckten Modells, die App streckt es aber auf die
 * Länge des Zustands. Eine Kachel soll 4 echte Meter breit bleiben, im UV-Raum also
 * 4/massstab Meter.
 */
export function wiederholungFuer(massstab: number): number {
  return massstab / KACHEL_M
}

/**
 * Three liest die Rauheit aus dem Grünkanal. Die Lieferung führt sie im Alphakanal, und
 * Grün trägt dort das Muster: ungepatcht wäre jede Fläche so rau wie sie hell ist.
 */
function rauheitAusAlpha(material: MeshStandardMaterial): void {
  material.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader.replace(
      'roughnessFactor *= texelRoughness.g;',
      'roughnessFactor *= texelRoughness.a;',
    )
  }
  // Ohne eigenen Schlüssel teilte sich das Material sein übersetztes Programm mit einem
  // ungepatchten Material derselben Bauart.
  material.customProgramCacheKey = () => 'rauheit-aus-alpha'
}

/**
 * Eine Kopie der Textur mit eigener Wiederholung. Die Kopie teilt sich das Bild mit dem
 * Original, three lädt es also kein zweites Mal auf die Grafikkarte; eigen ist nur die
 * Rechnung, die aus Metern Texturkoordinaten macht.
 */
const kopien = new Map<string, Texture>()

export function kachel(quelle: Texture, wiederholung: number): Texture {
  const schluessel = `${quelle.uuid}:${wiederholung.toFixed(4)}`
  const fertig = kopien.get(schluessel)
  if (fertig) return fertig
  const kopie = quelle.clone()
  kopie.wrapS = RepeatWrapping
  kopie.wrapT = RepeatWrapping
  kopie.repeat.set(wiederholung, wiederholung)
  kopie.needsUpdate = true
  kopien.set(schluessel, kopie)
  return kopie
}

/**
 * Legt die Texturen auf ein Material des geladenen Modells.
 *
 * `massstab` ist der Faktor, mit dem die App das Modell auf die Länge des Zustands
 * bringt. Eine Kachel soll 4 echte Meter breit bleiben, also 4/massstab Meter im
 * UV-Raum des Modells: die Wiederholung ist massstab/4.
 */
export function belegeMaterial(material: MeshStandardMaterial, massstab: number, quelle: TexturQuelle = textur): void {
  const belegung = belegungFuer(material.name, quelle)
  if (!belegung.farbe) return
  const wiederholung = wiederholungFuer(massstab)
  material.map = kachel(belegung.farbe, wiederholung)
  material.roughnessMap = material.map
  rauheitAusAlpha(material)
  if (belegung.normale) {
    material.normalMap = kachel(belegung.normale, wiederholung)
    material.normalScale.set(0.6, 0.6)
  }
  material.needsUpdate = true
}
